// frontend/app/components/lessonPlans/present/PresentNotes.js
//
// The teacher's own material: what to do, what the students should be doing,
// and the tips that name the misconception to watch for.
//
// On one mirrored screen there is no such thing as a private note — whatever
// the teacher sees, thirty students see, including the answer key. So this
// panel starts closed, and when it is open it says so out loud in its own
// header. A teacher who has forgotten the projector is mirroring their screen
// is the failure this component exists to prevent, and the honest fix is to
// tell them rather than to pretend the panel is private.
//
// The body sits at caption size, smaller than anything on the slide. Notes are
// an aside; they should not out-shout the lesson, and the size is the cue that
// they are not for the back row.

import React from "react";
import { ScrollView, StyleSheet, Text, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

const NoteSection = ({ type, colors, label, children }) => (
  <View style={styles.section}>
    <Text style={[styles.sectionLabel, type.chrome, { color: colors.teacherTipText }]}>
      {label}
    </Text>
    <Text style={[styles.sectionText, type.caption, { color: colors.textPrimary }]}>
      {children}
    </Text>
  </View>
);

const PresentNotes = ({ notes, colors, type, t }) => {
  const translate = t || ((value) => value);
  const { guidance, studentActivity, tips } = notes;
  if (!guidance && !studentActivity && tips.length === 0) return null;

  return (
    <View
      style={[
        styles.panel,
        {
          backgroundColor: colors.teacherTipBg,
          borderColor: colors.teacherTipBorder,
        },
      ]}
    >
      <View style={styles.header}>
        <MaterialCommunityIcons
          name="lightbulb-on-outline"
          size={16}
          color={colors.teacherTipText}
        />
        <Text style={[styles.headerText, type.chrome, { color: colors.teacherTipText }]}>
          {translate("Teacher notes — visible to the room")}
        </Text>
        <Text style={[styles.headerHint, type.chrome, { color: colors.textMuted }]}>
          {translate("Press N to hide")}
        </Text>
      </View>

      {/* Guidance can run long, and a note is never allowed to push the lesson
          off the wall — the panel is capped and scrolls inside itself. */}
      <ScrollView style={styles.scroll} showsVerticalScrollIndicator={false}>
        {guidance ? (
          <NoteSection type={type} colors={colors} label={translate("What you do")}>
            {guidance}
          </NoteSection>
        ) : null}

        {studentActivity ? (
          <NoteSection
            type={type}
            colors={colors}
            // "What students do" is the workspace's framing, where a student
            // is reading it about themselves. Here the teacher is reading it
            // about the room in front of them, and the same sentence has to
            // name who is doing what.
            label={translate("Do this with the class")}
          >
            {studentActivity}
          </NoteSection>
        ) : null}

        {tips.map((tip, index) => (
          <NoteSection
            key={tip.key || `tip-${index}`}
            type={type}
            colors={colors}
            label={translate("Teacher tip")}
          >
            {tip.text}
          </NoteSection>
        ))}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  panel: {
    borderRadius: 16,
    borderWidth: 2,
    paddingHorizontal: 20,
    paddingVertical: 14,
    maxHeight: 220,
  },
  header: { flexDirection: "row", alignItems: "center", marginBottom: 10 },
  headerText: {
    fontWeight: "800",
    letterSpacing: 0.8,
    textTransform: "uppercase",
    marginLeft: 8,
    flex: 1,
  },
  headerHint: { fontWeight: "600", marginLeft: 12 },

  scroll: { flexGrow: 0 },
  section: { marginBottom: 12 },
  sectionLabel: {
    fontWeight: "800",
    letterSpacing: 0.8,
    textTransform: "uppercase",
    marginBottom: 4,
  },
  sectionText: { fontWeight: "400" },
});

export default PresentNotes;
